import { useContext, useEffect, useState } from "react"
import { ProfileContext, ProfileContextPayload } from "../../Context/ProfileDetailsContext"
import GlobalPopUp from "../../GlobalComponent/GlobalPopup/GlobalPopup"
import RegisterProfile from "../../GlobalComponent/RegisterProfile/RegisterProfile"
import { Grid, IconButton, Typography } from "@mui/material"
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import GlobalButton from "../../Utils/GlobalUtilsComponents/GlobalButton"
import IconsColor from "../../Utils/IconsColor"
import sneakerIcon from "../../assets/Images/Profile/sneakerIcon.svg"
import editIcon from "../../assets/Images/Profile/pencilIcon.svg"
import profileDetailStyles from '../../Styles/ComponentStyles/ProfileStyle/ProfileDetails.module.scss'
import KickitLinkPopupWrapper from "../../GlobalComponent/KickitLinkPopup/KickitLinkPopupWrapper"
import { UserContext, UserContextPayload } from "../../Context/UserDetailContext"
import { useSnackbar } from "../../GlobalComponent/GlobalSnackbar/GlobalSnackbar"
import agent from "../../service/api"
import cloneDeep from "lodash/cloneDeep"
import { downloadPdfFromBase64 } from "../../Utils/downloadPdf"

const applyAnnuityProfileResponse = (
    response: any,
    pathName: string,
    setters: {
        setProfileName: (name: string) => void;
        setUserEmail: (email: string) => void;
        setIsSampleProfile: (value: boolean) => void;
        setFinalIncomeData: (data: any) => void;
        setSurplusDeficitChartData: (data: any) => void;
        setFinalData: (data: any) => void;
        setAccumulationPlanData: (data: any) => void;
        setProfileRothConversionDetails: (data: any) => void;
        setProfileUserDetails: (data: any) => void;
    }
) => {
    if (!response?.status) {
        return false;
    }

    const profileData = response?.inputData?.[0];
    if (profileData) {
        setters.setProfileName(profileData?.universalInput?.firstName ?? '');
        setters.setUserEmail(profileData?.universalInput?.email ?? '');
        setters.setIsSampleProfile(Boolean(profileData?.sample_profile ?? profileData?.sample_profile_flag));
    }

    if (pathName.includes('/annuityIncome')) {
        setters.setFinalIncomeData(response?.data?.[0]?.data);
        setters.setSurplusDeficitChartData(response?.data?.[0]?.graphDetails);
    } else if (pathName.includes('/annuityAccumulation')) {
        setters.setFinalData(response?.data?.[0]);
        setters.setAccumulationPlanData(response?.inputData?.[0]?.accumulationData);
    } else if (pathName.includes('/annuityRoth')) {
        const rothDetails = { data: response.data, inputData: response.inputData };
        setters.setProfileRothConversionDetails(rothDetails);
        setters.setProfileUserDetails({
            data: cloneDeep(response.data),
            inputData: cloneDeep(response.inputData),
        });
    }

    return true;
};

const AnnuityProfilesOutputWrapper = () => {

    const {
        flagOpenRegisterProfileDialog,
        setFlagOpenRegisterProfileDialog,
        profileName,
        setProfileName,
        setFlagOpenQuickEditDialog,
        userEmail,
        setUserEmail,
        getparticularUserProfileDetail,
        setFinalIncomeData,
        setSurplusDeficitChartData,
        setFinalData,
        setAccumulationPlanData,
        setProfileRothConversionDetails,
        setProfileUserDetails,
    } = useContext(ProfileContext) as ProfileContextPayload

    const {
        setFlagLoader
    } = useContext(UserContext) as UserContextPayload

    const [flagKickitBtnClicked, setFlagKickitBtnClicked] = useState(false);

    const { showSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const userId = searchParams.get('user_id') || searchParams.get('profile_id') || '';
    const userToken = searchParams.get('token');
    const pathName = location.pathname;
    const isIncomeProfile = pathName.includes('/annuityIncome');

    const [isSampleProfile, setIsSampleProfile] = useState(false);
    const [isDataLoaded, setIsDataLoaded] = useState(false);

    useEffect(() => {
        if (!userId || userToken) {
            setIsDataLoaded(true);
            return;
        }

        const fetchProfileDetails = async () => {
            setIsDataLoaded(false);
            try {
                setFlagLoader(true);
                const response = await getparticularUserProfileDetail(userId);
                const applied = applyAnnuityProfileResponse(response, pathName, {
                    setProfileName,
                    setUserEmail,
                    setIsSampleProfile,
                    setFinalIncomeData,
                    setSurplusDeficitChartData,
                    setFinalData,
                    setAccumulationPlanData,
                    setProfileRothConversionDetails,
                    setProfileUserDetails,
                });
                if (!applied) {
                    showSnackbar(response?.error?.message || 'Something went wrong!', 'error');
                }
            } catch (error) {
                console.error('Error fetching profile details:', error);
                showSnackbar('Something went wrong!', 'error');
            } finally {
                setFlagLoader(false);
                setIsDataLoaded(true);
            }
        };

        fetchProfileDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, pathName, userToken]);

    const handleEdit = () => {
        const currentPath = location.pathname;
        if (currentPath.includes('/annuityRoth')) {
            navigate(`/profile/newProfile/annuityRothConversion?user_id=${userId}`)
        } else if (currentPath.includes('/annuityIncome')) {
            navigate(`/profile/newProfile/annuityIncome?user_id=${userId}`)
        } else if (currentPath.includes('/annuityAccumulation')) {
            navigate(`/profile/newProfile/annuityAccumulation?user_id=${userId}`)
        }
    }

    const handleExportAsPdfBtnClick = async () => {
        try {
            setFlagLoader(true);
            const response = await agent.downloadProfilePdfAPI.get(userId);

            // Approach 1: If backend returns Base64 in JSON (current implementation)
            if (response?.status && response?.data?.pdfBase64) {
                const { fileName, pdfBase64 } = response.data;
                await downloadPdfFromBase64(pdfBase64, fileName);
            }
            else {
                showSnackbar(response?.error?.message || "Something went wrong!", "error")
            }
        } catch (error) {
            console.log(error);
            showSnackbar("Something went wrong!", "error")
        } finally {
            setFlagLoader(false);
        }
    }

    const renderActionButtons = () => {
        if (!isDataLoaded) {
            return null;
        }

        return (
            <Grid item className={`${profileDetailStyles.apptest}`}>
                <Grid container gap={"10px"}>
                    {isSampleProfile &&
                        <span
                            style={{
                                color: "#008236",
                                backgroundColor: "#DCFCE7",
                                border: "none",
                                borderRadius: "14px",
                                fontSize: "13px",
                                fontWeight: 600,
                                textAlign: "center",
                                padding: "10px"
                            }}
                        >
                            Sample Profile
                        </span>
                    }
                    {!isSampleProfile &&
                        <Grid item>
                            <GlobalButton
                                value="KickIt"
                                className={profileDetailStyles.shareBtn}
                                startIcon={<img src={sneakerIcon} alt=''></img>}
                                onClick={() => setFlagKickitBtnClicked(true)}
                            />
                        </Grid>
                    }
                    {pathName === "/profile/annuityProfileDetails/annuityRoth" &&
                        <>
                            <Grid item>
                                <GlobalButton
                                    value="Export As PDF"
                                    className={profileDetailStyles.quickEditBtn}
                                    onClick={() => { handleExportAsPdfBtnClick() }}
                                />
                            </Grid>
                            <Grid item>
                                <GlobalButton
                                    value="Quick Edit"
                                    variant="contained"
                                    className={profileDetailStyles.quickEditBtn}
                                    startIcon={<img src={editIcon} alt='' />}
                                    onClick={() => { setFlagOpenQuickEditDialog(true) }}
                                />
                            </Grid>
                        </>
                    }
                    <Grid item>
                        <GlobalButton
                            value="Edit"
                            className={profileDetailStyles.editBtn}
                            startIcon={<img src={editIcon} alt='' />}
                            onClick={() => { handleEdit() }}
                        />
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    return (
        <>
            {flagOpenRegisterProfileDialog &&
                <GlobalPopUp
                    open={flagOpenRegisterProfileDialog}
                    onClose={() => { setFlagOpenRegisterProfileDialog(false) }}
                    title='New Profile'
                    showCloseIcon={true}
                    flagDivider
                    maxWidth="sm"
                >
                    <RegisterProfile />
                </GlobalPopUp>
            }
            {flagKickitBtnClicked &&
                <GlobalPopUp
                    open={flagKickitBtnClicked}
                    onClose={() => { setFlagKickitBtnClicked(false) }}
                    title='KickIt'
                    showCloseIcon={true}
                    flagDivider
                    maxWidth="sm"
                >
                    <KickitLinkPopupWrapper userProfileId={userId} userEmail={userEmail} setFlagKickitBtnClicked={setFlagKickitBtnClicked} />
                </GlobalPopUp>
            }
            <Grid container>
                <Grid item xs={12} style={{ backgroundColor: "#fff", borderBottom: isIncomeProfile ? "1px solid #E2E8F0" : "none" }}>
                    <Grid container alignItems={"center"} justifyContent={"space-between"} padding={"20px"}>
                        <Grid item>
                            <Grid container alignItems={"center"} gap={'10px'}>
                                <Grid item>
                                    <IconButton
                                        onClick={() => {
                                            navigate(-1)
                                        }}>
                                        <IconsColor iconName="backArrow" color={"#45556C"} />
                                    </IconButton>
                                </Grid>
                                <Grid item>
                                    <Typography component={"span"} className={profileDetailStyles.profileName}>
                                        {profileName}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        {renderActionButtons()}
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Outlet />
                </Grid>
            </Grid>
        </>
    )
}

export default AnnuityProfilesOutputWrapper
